import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { selectVoiceLiveDisplayState } from '../utils';
import type { AvatarState, ConnectionState, TokenUsageSummary } from '../types';
import { type AvatarPeerControls, useAvatarFallback } from './useAvatarFallback';
import { useAvatarPeerConnection } from './useAvatarPeerConnection';
import { useConversationTranscript } from './useConversationTranscript';
import { useDevFallbackToggle } from './useDevFallbackToggle';
import { useTokenUsage } from './useTokenUsage';
import { useVoiceLiveAudio } from './useVoiceLiveAudio';
import { useVoiceLiveConnection } from './useVoiceLiveConnection';
import { useVoiceLiveTelemetry } from './useVoiceLiveTelemetry';

export interface UseVoiceLiveSessionOptions {
  /** Authenticated user id. */
  userId: string;
  /** Scenario id for the roleplay. */
  scenarioId: string;
  /** Whether video is enabled when the session starts. */
  defaultVideoEnabled: boolean;
  /** Called when the FieldOne session id is created. */
  onSessionCreated?: (sessionId: number) => void;
}

export interface UseVoiceLiveSessionResult {
  connectionState: ConnectionState;
  isActive: boolean;
  retryCount: number;
  handleStart: (userId: string, isReconnect?: boolean) => Promise<void>;
  handleStop: () => void;
  handleManualRetry: () => void;
  avatarState: AvatarState;
  videoRef: MutableRefObject<HTMLVideoElement | null>;
  isDegraded: boolean;
  isVideoEnabled: boolean;
  handleModeChange: (videoSelected: boolean) => void;
  showAudioVisualizer: boolean;
  showConnectionOverlay: boolean;
  errorMessage: string;
  inputAnalyser: AnalyserNode | null;
  outputAnalyser: AnalyserNode | null;
  devToolsVisible: boolean;
  forceAudioFallback: boolean;
  setForceAudioFallback: Dispatch<SetStateAction<boolean>>;
  forceIceTimeout: boolean;
  setForceIceTimeout: Dispatch<SetStateAction<boolean>>;
  showTokenUsage: boolean;
  setShowTokenUsage: Dispatch<SetStateAction<boolean>>;
  tokenUsageSummary: TokenUsageSummary | null;
}

/**
 * Composition root for the Voice Live training session. Wires together the
 * telemetry, audio, transcript, dev-toggle, avatar-fallback, avatar-peer, and
 * WebSocket-connection hooks (resolving their mutual dependencies via late-bound
 * refs) and exposes the flat state + handlers the presentation component renders.
 */
export const useVoiceLiveSession = ({
  userId,
  scenarioId,
  defaultVideoEnabled,
  onSessionCreated,
}: UseVoiceLiveSessionOptions): UseVoiceLiveSessionResult => {
  // Telemetry: App Insights instance + stable session context (backend host,
  // connect-start time, ICE server URLs). See useVoiceLiveTelemetry.
  const telemetry = useVoiceLiveTelemetry({ scenarioId });

  const [isVideoEnabled, setIsVideoEnabled] = useState(defaultVideoEnabled);

  // Session-level state shared across the avatar / connection subsystems.
  const [avatarIceServers, setAvatarIceServers] = useState<RTCIceServer[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  // DEV-only "force audio fallback" toggle (revealed on localhost or with
  // ?forceAudioFallback=1). See useDevFallbackToggle.
  const {
    forceAudioFallback,
    setForceAudioFallback,
    devToolsVisible,
    forceAudioFallbackRef,
    forceIceTimeout,
    setForceIceTimeout,
    forceIceTimeoutRef,
    showTokenUsage,
    setShowTokenUsage,
  } = useDevFallbackToggle();
  const wsRef = useRef<WebSocket | null>(null);

  // Client-side audio subsystem (AudioContext, mic capture, input/output meter
  // analysers, audio-only fallback playback). Owned by useVoiceLiveAudio.
  const audio = useVoiceLiveAudio();
  const {
    inputAnalyser,
    outputAnalyser,
    tapOutputFromStream: tapOutputAnalyser,
    stopPlayback: stopFallbackAudioPlayback,
  } = audio;

  const fieldOneSessionIdRef = useRef<number | null>(null);

  // Mirrors isVideoEnabled for stale-closure-free reads inside startAvatar.
  const isVideoEnabledRef = useRef<boolean>(defaultVideoEnabled);

  // Auto-start management (shared with useVoiceLiveConnection)
  const autoStartAvatarRef = useRef<boolean>(false);
  const iceServerTimeoutRef = useRef<number | null>(null);
  const hasIceServersRef = useRef<boolean>(false);
  const iceServersRef = useRef<RTCIceServer[]>([]);

  // Reconnection management (shared with useVoiceLiveConnection)
  const shouldReconnectRef = useRef<boolean>(true);
  const savedUserIdRef = useRef<string>('');

  // Frontend-owned transcript & start time (source of truth across pods).
  // Owned by useConversationTranscript.
  const transcriptStore = useConversationTranscript();

  // DEV-only live LLM token usage readout state. Owned by useTokenUsage.
  const tokenUsage = useTokenUsage();

  // Late-bound peer controls + connection (re)start, wired below once the peer
  // hook and handleStart exist — breaks the peer ↔ fallback ↔ connection recursion.
  const peerRef = useRef<AvatarPeerControls | null>(null);
  const handleStartRef = useRef<(userId: string, isReconnect?: boolean) => void>(() => {});

  // Audio-only fallback gating state machine (retry → degraded fallback).
  // Owned by useAvatarFallback; the peer hook reads its gating refs.
  const fallback = useAvatarFallback({
    defaultVideoEnabled,
    telemetry,
    peerRef,
    handleStartRef,
    wsRef,
    iceServersRef,
    iceServerTimeoutRef,
    autoStartAvatarRef,
    savedUserIdRef,
    fieldOneSessionIdRef,
    isVideoEnabledRef,
    setIsVideoEnabled,
    setErrorMessage,
    stopFallbackAudioPlayback,
  });
  const {
    isAudioFallbackRef,
    avatarEverActiveRef,
    initialAvatarFailuresRef,
    currentAttemptFailedRef,
    isDegraded,
    failAvatarEstablishment,
    reattemptAvatarFromFallback,
  } = fallback;

  // WebRTC avatar peer connection (RTCPeerConnection, media elements, SDP
  // exchange, track + connection-state handling). Owned by useAvatarPeerConnection.
  const peer = useAvatarPeerConnection({
    userId,
    telemetry,
    wsRef,
    avatarIceServers,
    hasIceServersRef,
    isVideoEnabledRef,
    forceFailRef: forceAudioFallbackRef,
    forceIceTimeoutRef,
    shouldReconnectRef,
    avatarEverActiveRef,
    currentAttemptFailedRef,
    initialAvatarFailuresRef,
    tapOutputFromStream: tapOutputAnalyser,
    setErrorMessage,
    onInitialFailure: failAvatarEstablishment,
  });
  const {
    avatarState,
    setAvatarState,
    videoRef,
    startAvatar,
    cleanupAvatar,
    detachVideo,
    attachStoredVideo,
    hasVideoTransceiver,
  } = peer;

  // Wire the late-bound peer controls for the fallback hook.
  peerRef.current = { startAvatar, cleanupAvatar, setAvatarState };

  // WebSocket session lifecycle, reconnection, and server-message dispatch.
  // Owned by useVoiceLiveConnection.
  const { connectionState, isActive, retryCount, handleStart, handleStop, handleManualRetry } =
    useVoiceLiveConnection({
      scenarioId,
      userId,
      onSessionCreated,
      wsRef,
      fieldOneSessionIdRef,
      isVideoEnabledRef,
      autoStartAvatarRef,
      iceServerTimeoutRef,
      hasIceServersRef,
      iceServersRef,
      shouldReconnectRef,
      savedUserIdRef,
      setErrorMessage,
      setAvatarIceServers,
      telemetry,
      transcriptStore,
      tokenUsage,
      audio,
      peer,
      fallback,
    });

  // Late-bind handleStart for the fallback hook.
  handleStartRef.current = handleStart;

  // Keep the ref in sync so startAvatar reads the latest video preference.
  useEffect(() => {
    isVideoEnabledRef.current = isVideoEnabled;
  }, [isVideoEnabled]);

  /**
   * Toggle video on/off without disrupting the peer connection or audio.
   *
   * Video → audio  Detach srcObject so the browser stops decoding frames.
   *                The RTCPeerConnection and audio element stay alive —
   *                no reconnect, no audio gap, avatarState remains 'active'
   *                so the visualizer appears immediately.
   *
   * Audio → video  If the peer already has a video track, re-attach the
   *                stored MediaStream — also instant.
   *                If the peer has no video transceiver (session started
   *                with defaultVideoEnabled=false), a full peer reconnect
   *                is required to negotiate the video track.
   */
  const handleVideoModeToggle = (newVideoEnabled: boolean) => {
    isVideoEnabledRef.current = newVideoEnabled;
    setIsVideoEnabled(newVideoEnabled);

    if (!newVideoEnabled) {
      // Switching to audio-only: detach video srcObject so the browser stops
      // decoding frames. Peer and audio element remain completely untouched.
      detachVideo();
    } else {
      // Switching to video. Re-attach the stored stream if the peer already has
      // a video track (instant); otherwise, if the peer was negotiated without a
      // video transceiver, reconnect it to add one (brief audio interruption).
      if (!attachStoredVideo() && !hasVideoTransceiver() && iceServersRef.current.length > 0) {
        cleanupAvatar();
        startAvatar(iceServersRef.current);
      }
      // If no peer yet, the next startAvatar call will use isVideoEnabledRef.current.
    }
  };

  // Avatar-vs-audio display flags derived from the current connection, avatar,
  // video, and degraded states (see selectVoiceLiveDisplayState for the rules).
  const { showAudioVisualizer, showConnectionOverlay } = selectVoiceLiveDisplayState({
    connectionState,
    avatarState,
    isVideoEnabled,
    isDegraded,
  });

  // Resolve a video/audio mode change: re-attempt the WebRTC avatar when coming
  // from the degraded/audio-only state, otherwise toggle modes in place.
  const handleModeChange = (videoSelected: boolean) => {
    if (videoSelected && (isDegraded || isAudioFallbackRef.current)) {
      reattemptAvatarFromFallback();
    } else {
      handleVideoModeToggle(videoSelected);
    }
  };

  return {
    connectionState,
    isActive,
    retryCount,
    handleStart,
    handleStop,
    handleManualRetry,
    avatarState,
    videoRef,
    isDegraded,
    isVideoEnabled,
    handleModeChange,
    showAudioVisualizer,
    showConnectionOverlay,
    errorMessage,
    inputAnalyser,
    outputAnalyser,
    devToolsVisible,
    forceAudioFallback,
    setForceAudioFallback,
    forceIceTimeout,
    setForceIceTimeout,
    showTokenUsage,
    setShowTokenUsage,
    tokenUsageSummary: tokenUsage.summary,
  };
};
